import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import s from './styles.module.scss';

import type { IBarChartData } from '../../types';

interface IProps {
  data: IBarChartData[];
  tooltip?: (props: any) => React.ReactNode;
  xTickFormatter?: (props: any) => string;
  hoverBackgroundColor?: string;
  width?: string;
  height?: string | number;
}

export const BaseBarChartStacked: React.FC<IProps> = (props) => {
  const {
    data,
    tooltip,
    hoverBackgroundColor,
    xTickFormatter,
    width = '100%',
    height = '100%',
  } = props;
  const barMetadata = useMemo(() => {
    const map = new Map<string, { color: string; index: number }>();

    data.forEach((entry) => {
      entry.bars.forEach((bar) => {
        if (!map.has(bar.name)) {
          map.set(bar.name, {
            color: bar.color,
            index: bar.index ?? Number.MAX_SAFE_INTEGER,
          });
        }
      });
    });

    return Array.from(map.entries())
      .sort(([, a], [, b]) => a.index - b.index)
      .map(([name, meta]) => ({ name, ...meta }));
  }, [data]);

  const mappedData = useMemo(() => {
    return data.map((entry) => {
      const result: Record<string, number> = {};
      const colorMap: Record<string, string> = {};

      entry.bars.forEach((bar) => {
        result[bar.name] = bar.value;
        colorMap[bar.name] = bar.color;
      });

      return {
        name: entry.name,
        ...result,
        colors: colorMap,
      };
    });
  }, [data]);

  return (
    <ResponsiveContainer width={width} height={height} className={s.chart}>
      <BarChart data={mappedData}>
        <CartesianGrid strokeDasharray="1 1" />
        <XAxis dataKey="name" tickFormatter={xTickFormatter} />
        <YAxis />
        <Tooltip content={tooltip} cursor={{ fill: hoverBackgroundColor }} />

        {barMetadata.map(({ name, color }) => (
          <Bar key={name} dataKey={name} name={name} stackId="a" fill={color} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
